// CultivarsListTable.js

import React, { useState, useEffect } from 'react';
import { Layer, Box, DataTable, TextInput, Text, Image, Button,
        ResponsiveContext, Heading, Card, CardBody, CardHeader } from 'grommet';
import { calculateHeight, usePopupFocus } from '../Utils/Utils';
import HomePageNav from '../Base components/HomePageNav';
import { Edit, Search } from 'grommet-icons';
import styled from 'styled-components';
import { Pagination as GrommetPagination } from 'grommet';
import CultivarDetails from './CultivarDetails';

// Styled component for Pagination to customize controls
const StyledPagination = styled(GrommetPagination)`
    button {
        color: ${(props) => props.theme.global.colors.brand};  
        svg {
            stroke: ${(props) => props.theme.global.colors.brand};
        }
    }

    button:hover {
        // color: ${(props) => props.theme.global.colors.brand};
        // background-color: ${(props) => props.theme.global.colors.brand};
        font-weight: bold; /* Apply bold to all page numbers */
        font-size: 1.2em; /* Example: Make active page number larger */ 
    }   
`;

const CultivarsTable = ({ cultivarsList, cultivarFullPropertiesList, loadCultivarsList }) => {
    const [filteredCultivars, setFilteredCultivars] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(13);

    const [selectedCultivar, setSelectedCultivar] = useState(null); // State to store selected cultivar
    const [showCultivarDetails, setShowCultivarDetails]  = useState(false); // show popup with CultivarDetails 

    // Manage focus when opening modal windows
    usePopupFocus(showCultivarDetails, "cult-det");

    useEffect(() => {
        if (cultivarsList && cultivarsList.length > 0) {
            setFilteredCultivars(cultivarsList);
        }
    }, [cultivarsList]);


    useEffect(() => {
        if (!searchQuery) {
            setFilteredCultivars(cultivarsList);
        } else {
            // console.log('searchQuery   >>>  ', searchQuery.toLowerCase().trim());
            const lowerCaseQuery = searchQuery.toLowerCase().trim();
            const filtered = cultivarsList.filter(cultivar => {
                // Log the cultivar for debugging
                // console.log('Checking cultivar:', cultivar);
                return Object.entries(cultivar).some(([key, value]) => {
                    // Exclude 'CultivarID' from the search
                    if (key === 'CultivarID') return false;
    
                    // Check if value exists and includes the search query
                    const normalizedValue = value ? value.toString().toLowerCase() : '';
                    const match = normalizedValue.includes(lowerCaseQuery);
                    // Log the match result (only if match is true)
                    // match && console.log(`Key: "${key}", Value: "${normalizedValue}", Match: ${match}`);
                    return match;
                });
            });
            setFilteredCultivars(filtered);
        }
    }, [searchQuery, cultivarsList]);

    // Function to create a new cultivar
    const createNewCultivar = () => {
        const cultivar = {
            // Id: generateSyncId(),
            CultivarID: null,
            CultivarName: '',
            Brand: '',
            Mother: '',
            Father: '',
            RootstockId: null,
            Type: null,
            Harvest: null,
            BudBreak: null,
            RainResistance: null,
            Chilling: null,
            CreateDate: null,
            CreatedBy: null,
            IsCommercial: 1,
            CommercialFromDate: null,
            SetAsCommercialBy: null,
            AddDate: null,
            AddedBy: '',
            Country: '',
            Company:'',
            Notes: '',
            LastSyncDate: null
        };
            
        // Set the selected cultivar in state
        setSelectedCultivar(cultivar);
    
        // Show the cultivar details
        setShowCultivarDetails(true);
    };


    const generateColumns = (paginatedCultivars) => {
        if (paginatedCultivars.length === 0) return [];
    
        // Create columns dynamically based on keys of the first cultivar in the list
        const dynamicColumns = [
            {
                property: "rowIndex",
                header: "No.",
                render: (datum) => (
                <Box pad={{ vertical: "xxsmall" }}>
                    <Text size="small">{datum.rowIndex}</Text>
                </Box>
                ),
            },
            ...Object.keys(paginatedCultivars[0])
            .filter((key) => key !== "CultivarID") // Exclude the 'CultivarID' key
            .map((key) => {
                // Convert camelCase or other cases to space-separated words for header label
                const headerLabel = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
        
                return {
                    property: key,
                    header: (
                        <Box>
                            <Text style={{ whiteSpace: 'nowrap' }}>{headerLabel}</Text>
                        </Box>
                    ),
                    render: (datum) => (
                        <Box pad={{ vertical: "xxsmall" }}>
                        <Text size="15px" style={{whiteSpace: 'nowrap'}}>{datum[key]}</Text>
                        </Box>
                    ),
                };
            }),
            {
                property: "edit",
                header: "",
                render: (datum) => (
                    <Box
                        round="full" // Full round circle
                        border={{ color: 'brand', size: 'xsmall' }} // Adjust border color and size
                        align="center"
                        justify="center"
                        pad={{ horizontal: 'small',  vertical: 'xxsmall'}}
                        style={{ display: 'inline-flex' }} // Ensure Box behaves like inline-flex
                        onClick={() => handleEditClick(datum)} 
                    >
                        <Button
                            // onClick={() => handleEditClick(datum)} 
                            secondary
                            style={{ 
                                padding: '0', 
                                fontSize: '12px', 
                                background: 'transparent', // Make background transparent to see the border
                                border: 'none', // Remove default button border
                            }}
                            icon={<Edit size='16px'/>}
                        />
                    </Box>
                ),
            }
        ];
    
        return dynamicColumns;
    };

    const handleEditClick = (cultivar) => {
        // console.log('cultivar >> ', cultivar);

        // Find the selected cultivar from props.cultivarFullPropertiesList based on CultivarID
        const selectedCultivar = cultivarFullPropertiesList.find(
            (item) => item.CultivarID === cultivar.CultivarID
        );
    
        // Set the selected cultivar in state
        setSelectedCultivar(selectedCultivar);
    
        // Show the cultivar details
        setShowCultivarDetails(true);
    };    

    const paginatedCultivars = filteredCultivars.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handlePageChange = ({ page }) => setCurrentPage(page);

    return (
        <ResponsiveContext.Consumer>
        {screenSize => (
        <Box
            width="90vw" 
            height="100vh"
            background="light-1"
            align="center"
            >
            <Box 
                width="90%" 
                height="auto" 
                margin="medium"
                pad="medium"
                round="xxsmall" 
                background="light-1"
              //  elevation="small" 
            >
                <CardHeader pad={{ horizontal: 'medium' }} align="center" justify="center">
                <Heading level="2" margin={{ vertical: 'xxsmall' }}>Cultivars Information</Heading>
                </CardHeader>
                <CardBody align="center">
                
                <Box width="90%" height="700px">
                    <Box flex
                        width="100%"
                        direction="row-responsive"
                        pad={{ vertical: 'medium' }}
                        margin={{ vertical: 'medium' }}
                        //  wrap={true} // Allow wrapping on small screens
                        gap="medium" // Adds spacing between items in a responsive way
                        justify='between'
                        align="center">
                        {/* Search Input */}
                        <Box width="medium" margin="small" >
                            <TextInput
                                style={{padding: "8px"}}
                                placeholder="Search Cultivar..."
                                value={searchQuery}
                                onChange={event => setSearchQuery(event.target.value)}
                                icon={<Search />}
                                reverse // This moves the icon to the end (after the text)
                            />
                        </Box>
                        <Box margin="small">
                            <Button secondary label="Add new" onClick={createNewCultivar}  
                                style={{ paddingTop: '2px', paddingBottom: '2px', fontSize: '16px', color: '#00739D'}} />                    
                        </Box>
                    </Box>

                    {/* Cultivars Table */}
                    <Box width="100%" height="large" overflow="auto"
                    //  margin={{ vertical: 'medium' }}
                    >
                    {paginatedCultivars.length > 0 ? (
                        <DataTable
                        columns={generateColumns(paginatedCultivars)}
                        data={paginatedCultivars.map((cultivar, index) => ({
                            ...cultivar,
                            rowIndex: index + 1,    // Add row index for numbering
                        }))}
                        pad={{ vertical: 'xxsmall', horizontal: 'small' }} 
                        rowProps={{
                            even: { background: 'light-3', pad: { vertical: 'xsmall' } },
                            odd: { background: 'light-1', pad: { vertical: 'xsmall' } }
                        }}
                        border={{ header: { side: 'horizontal' }, body: { side: 'bottom' } }}
                        background={{
                            header: '#cfeffa',
                            body: ['light-1', 'light-2']
                        }}
                        />
                    ) : (
                        <Box
                            fill
                            align="center"
                            justify="center"
                            background={{
                                color: "light-2",           // Keep the background color
                                image: "url('BgPic.png')", // Add the background image
                                size: "contain",            // Control the size of the background image
                                repeat: "no-repeat",        // Prevent the image from repeating
                                opacity: "10%",          // Adjust the opacity of the background image
                            }}
                            >
                            <Text
                                size="large"
                                weight="bold"
                                color="brand"
                            >
                                No cultivars found.
                            </Text>
                        </Box>
                    )}
                    </Box>

                    {/* Styled Pagination */}
                    {filteredCultivars.length > pageSize && (
                    <StyledPagination
                        numberItems={filteredCultivars.length}
                        page={currentPage}
                        step={pageSize}
                        onChange={handlePageChange}
                    /> )}
                </Box>
                </CardBody>
            </Box>
            {/* Show CultivarDetails Modal */}
            {showCultivarDetails && 
                <CultivarDetails
                    cultivar={selectedCultivar} // Pass the selected cultivar to CultivarDetails 
                    onClosePopup={() => setShowCultivarDetails(false)} 
                    createFromStation={false}
                    loadCultivarsList={loadCultivarsList}
                />                     
            } 
                               
        </Box>
        )}
        </ResponsiveContext.Consumer>
    );
};

const CultivarsListTable = ({  cultivarsList, cultivarFullPropertiesList, loadCultivarsList }) => {

    return (
        <ResponsiveContext.Consumer>
            {screenSize => (
                <Box width="100%" background="#ffffff" alignSelf="center">
                    <Box
                        flex
                        fill="vertical"
                        style={{
                        position: 'fixed',
                        top: 0,
                        zIndex: 10,
                        height: calculateHeight(5)
                        }}
                    >
                        <Box justify="center">
                        <HomePageNav />
                        </Box>
                    </Box>
                    <Box>
                        <Box width="98%" margin="0 auto" style={{ height: "40px", minHeight: "40px" }}>
                            <Box style={{ position: "relative" }}>
                                <Box background="neutral-3" pad={{ vertical: "small" }} margin={{ vertical: "xxsmall" }}>
                                    <Box direction="row" justify="center" align="center">
                                    <Text size="large" weight="bold" color="#ffffff">Cultivars Management</Text>
                                    </Box>
                                </Box>
                                <Box
                                    width={
                                        {
                                        xsmall: "60px",
                                        small: "90px",
                                        medium: "90px",
                                        large: "120px",
                                        xlarge: "120px"
                                        }[screenSize] || "120px"
                                    }
                                    height={
                                        {
                                        xsmall: "60px",
                                        small: "90px",
                                        medium: "90px",
                                        large: "120px",
                                        xlarge: "120px"
                                        }[screenSize] || "120px"
                                    }
                                    style={{ position: "absolute", right: "3%", top: "10px" }}
                                >
                                <Image fit="contain" src="Grape.png" />
                                </Box>
                            </Box>
                        </Box>

                        <Box align="center" justify="center" width="100%">
                        <CultivarsTable 
                            cultivarsList={cultivarsList} 
                            cultivarFullPropertiesList={cultivarFullPropertiesList}
                            loadCultivarsList={loadCultivarsList}/>
                        </Box>
                    </Box>
                </Box>

            )}
        </ResponsiveContext.Consumer>
    );
};

export default CultivarsListTable;